<template>
  <sf-dialog v-model="dialog">
    <template #activator="{ props: activatorProps }">
      <sf-text-button v-bind="activatorProps" small @click="trackClick">{{
        $t('serialNumbers.serialNumbers')
      }}</sf-text-button>
    </template>

    <v-card>
      <v-card-item>
        <v-card-title>
          <sf-heading>{{ $t('serialNumbers.serialNumbers') }}</sf-heading>
        </v-card-title>
      </v-card-item>
      <v-card-text class="pt-1 d-flex flex-column">
        <sf-text style="white-space: pre-line" v-html="sanitizedSerialsText" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <sf-text-button :disabled="!isSupported" @click="copyToClipboard">{{
          $t('serialNumbers.copyAll')
        }}</sf-text-button>
      </v-card-actions>
    </v-card>
  </sf-dialog>
</template>

<script setup lang="ts">
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfDialog from '@/components/dialog/SfDialog.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import SfText from '@/components/text/SfText.vue'
import helpers from '@/modules/message/helpers'
import OrderEvent from '@/modules/tracking/events/order'
import { trackEvent } from '@/modules/tracking/useTracking'
import { SerialNumber } from '@/types/index'
import { useClipboard } from '@vueuse/core'
import DOMPurify from 'dompurify'
import { computed, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  serials: {
    type: Array as PropType<SerialNumber[]>,
    required: true
  }
})
const { t } = useI18n()
const dialog = ref(false)
const { copy, isSupported } = useClipboard()

const serialsText = computed(() => {
  let text = ''
  props.serials.forEach((serial, index) => {
    if (index > 0) {
      text += '\n'
    }
    if (serial.type) {
      text += serial.type + ': '
    }
    text += serial.value
  })

  return text
})

const sanitizedSerialsText = computed(() =>
  DOMPurify.sanitize(serialsText.value.replace(/\n/g, '<br />'))
)

const trackClick = () => {
  trackEvent(OrderEvent.OpenSerialNumbers)
}

const copyToClipboard = () => {
  trackEvent(OrderEvent.CopySerialNumbers)
  copy(serialsText.value).then(() => helpers.reportSuccess(t('serialNumbers.serialNumbersCopied')))
}
</script>
